'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Badge, Card, Image, buttonVariants } from '@/components/ui';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { cn } from '@/lib/utils';
import {
  directionsHref,
  formatTimeOfDay,
  telHref,
  venueMapEmbedHref,
  type VenuePageItem,
} from '../../libs/venuePage';

export function PublicVenueDetail({
  item,
  position,
  total,
  locale,
}: {
  item: VenuePageItem;
  position: number;
  total: number;
  locale: string;
}) {
  const t = useTranslations('Event.venuesPage');
  const { venue: v, courts, today, scheduleHref } = item;
  const [failedPhoto, setFailedPhoto] = useState<string | null>(null);
  const photo = v.photoUrl && v.photoUrl !== failedPhoto ? v.photoUrl : null;
  const map = venueMapEmbedHref(v);
  const courtsText =
    courts.kind === 'range'
      ? t('courtsRange', { from: courts.from, to: courts.to })
      : courts.kind === 'list'
        ? courts.names.length === 1
          ? courts.names[0]
          : t('courtsList', { names: courts.names.join(', ') })
        : null;
  const external = { target: '_blank', rel: 'noopener noreferrer' } as const;
  const button = (variant: 'primary' | 'outline') =>
    cn(
      buttonVariants.base,
      buttonVariants.variants.variant[variant],
      'min-h-11 max-w-full px-4 text-center break-words',
    );
  return (
    <section data-public-venue-detail={v.id} className="min-w-0">
      {/* The spacer and title share a grid row: its height is the larger of the photo ratio
          and full title content, so enlarged/long text cannot be clipped by a fixed ratio. */}
      <div
        data-venue-identity
        className={cn(
          'relative grid min-w-0 grid-cols-1 overflow-hidden rounded-2xl print:min-h-0',
          photo
            ? "before:col-start-1 before:row-start-1 before:aspect-[16/9] before:content-[''] lg:before:aspect-[21/8] print:before:hidden"
            : 'bg-surface',
        )}
      >
        {photo && (
          <Image
            key={photo}
            src={photo}
            alt={t('photoAlt', { name: v.name })}
            fill
            className="object-cover print:hidden"
            sizes="(max-width: 768px) 100vw, 1000px"
            onError={() => setFailedPhoto(photo)}
          />
        )}
        <div
          className={cn(
            'relative col-start-1 row-start-1 min-w-0 self-end px-5 pb-5 lg:px-7',
            photo
              ? 'text-secondary-foreground from-secondary/90 via-secondary/75 print:text-foreground bg-gradient-to-t via-[calc(100%-3.5rem)] to-transparent pt-14 print:bg-none print:pt-5'
              : 'text-foreground pt-5',
          )}
        >
          <h2 className="font-heading text-2xl leading-tight font-extrabold break-words lg:text-4xl">
            {v.name}
          </h2>
          <p
            className={cn(
              'mt-2 font-mono text-[0.6875rem] tracking-wide uppercase',
              !photo && 'text-muted',
            )}
          >
            {total > 1 && (
              <>
                {t('stopOf', { n: position, total })}
                {' · '}
              </>
            )}
            {t('courtCount', { count: item.courtCount })}
          </p>
        </div>
      </div>
      <div
        className={cn(
          'mt-5 min-w-0 gap-6 space-y-5 lg:space-y-0 print:block',
          map && 'lg:grid lg:grid-cols-[minmax(0,55fr)_minmax(0,45fr)] lg:items-start',
        )}
      >
        <Card className="min-w-0 space-y-5 rounded-2xl p-5 lg:p-6 print:shadow-none">
          {v.note && (
            <div className="text-sm">
              <h3 className="sr-only">{t('goodToKnow')}</h3>
              <p className="text-foreground leading-relaxed break-words whitespace-pre-line">
                {v.note}
              </p>
            </div>
          )}
          {v.address && (
            <p className="text-muted text-sm break-words whitespace-pre-line">{v.address}</p>
          )}
          <div className="grid min-w-0 grid-cols-2 gap-2">
            <a
              className={cn(button('primary'), 'col-span-2 w-full lg:w-auto lg:justify-self-start')}
              href={directionsHref(v)}
              {...external}
            >
              <span className="min-w-0 break-words">{t('directions')}</span>
            </a>
            {v.location?.website && (
              <a
                className={cn(button('outline'), 'w-full')}
                href={v.location.website}
                {...external}
              >
                <span className="min-w-0 break-words">{t('website')}</span>
              </a>
            )}
            {v.location?.phone && (
              <a
                className={cn(button('outline'), 'w-full')}
                href={telHref(v.location.phone)}
                aria-label={t('callAria', { phone: v.location.phone })}
                {...external}
              >
                <span className="min-w-0 break-words">{t('call')}</span>
              </a>
            )}
          </div>
          {courtsText && (
            <p className="border-border border-t pt-4 text-sm font-medium break-words">
              {courtsText}
            </p>
          )}
          {!!item.courtWatchLinks?.length && (
            <ul className="border-border space-y-2 border-t pt-4">
              {item.courtWatchLinks.map((watch) => (
                <li
                  key={watch.courtId}
                  className="flex min-w-0 flex-wrap items-center gap-2 text-sm"
                >
                  <span className="min-w-0 flex-1 font-medium break-words">{watch.courtName}</span>
                  <Badge variant={watch.state === 'live' ? 'success' : 'outline'}>
                    {t(watch.state === 'live' ? 'watchLive' : 'watchReplay')}
                  </Badge>
                  <a
                    className={button('outline')}
                    href={watch.url}
                    {...external}
                    aria-label={t(watch.state === 'live' ? 'watchLiveAria' : 'watchReplayAria', {
                      court: watch.courtName,
                    })}
                  >
                    {t('watch')}
                  </a>
                </li>
              ))}
            </ul>
          )}
          {today && scheduleHref && (
            <div className="border-border border-t pt-4 text-sm">
              <p className="break-words">
                <span className="font-semibold">{t('todayHere')}</span>
                {' · '}
                {t('matchCount', { count: today.count })}
                {' · '}
                {t.rich('firstMatch', {
                  time: () => (
                    <LocalDateTime iso={today.firstAt} locale={locale} format={formatTimeOfDay} />
                  ),
                })}
              </p>
              <a
                href={scheduleHref}
                className="text-accent inline-flex min-h-11 items-center font-medium underline"
              >
                {t('scheduleLink')}
              </a>
            </div>
          )}
        </Card>
        {map && (
          <Card className="flex min-w-0 flex-col overflow-hidden rounded-2xl p-0 print:hidden">
            <div className="flex min-h-11 min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-2">
              <h3 className="text-muted min-w-0 text-xs font-normal break-words">{v.address}</h3>
              <a
                className={cn(
                  buttonVariants.base,
                  buttonVariants.variants.variant.outline,
                  "relative h-6 min-h-6 max-w-full px-2 py-0 font-mono text-[0.6875rem] uppercase before:absolute before:inset-x-0 before:-inset-y-2.5 before:content-['']",
                )}
                href={directionsHref(v)}
                {...external}
              >
                <span className="min-w-0 break-words">{t('openMap')}</span>
              </a>
            </div>
            <iframe
              key={map}
              src={map}
              title={t('mapTitle', { name: v.name })}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="h-[25rem] w-full border-0"
            />
          </Card>
        )}
      </div>
    </section>
  );
}
